/**
 * Admin-review API client + cache invalidation helper (Block 6.F.5).
 *
 * Wraps GET /v1/admin-review/timeline + GET /v1/admin-review/badge-count
 * + the new archive/restore endpoints (DELETE /v1/providers/{id} + POST
 * /v1/providers/{id}/restore + POST /v1/individuals/{id}/restore).
 *
 * Cache invalidation policy per design plan r2 §7.8: every mutation
 * that emits change_log / creates provider_notes / writes suggested_edits
 * invalidates BOTH `['admin-review', 'timeline']` AND
 * `['admin-review', 'badge-count']` keys on success. Helpers below
 * centralize the key shape so callers don't drift.
 */

import { writeFetch } from './writeFetch';
import type { QueryClient } from '@tanstack/react-query';
import type {
  AdminBadgeCountResponse,
  AdminTimelineResponse,
} from './types';
import { apiErrorFrom } from './api';

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function getJson<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(tenantUrl(slug, path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `GET ${path}`);
  return (await r.json()) as T;
}

async function postNoBody<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const r = await writeFetch(tenantUrl(slug, path), {
    method: 'POST',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `POST ${path}`);
  return (await r.json()) as T;
}

async function deleteJson<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const r = await writeFetch(tenantUrl(slug, path), {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!r.ok) throw await apiErrorFrom(r, `DELETE ${path}`);
  return (await r.json()) as T;
}

// =====================================================================
// adminReviewApi
// =====================================================================

export const adminReviewApi = {
  /**
   * Recent activity timeline. Steward-gated server-side; non-stewards get 403.
   * Optional actor_user_id for the "Mine" chip filter.
   */
  timeline(
    slug: string,
    params: { actor_user_id?: string; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminTimelineResponse> {
    const sp = new URLSearchParams();
    if (params.actor_user_id) sp.set('actor_user_id', params.actor_user_id);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.offset !== undefined) sp.set('offset', String(params.offset));
    const qs = sp.toString();
    return getJson(slug, `/admin-review/timeline${qs ? `?${qs}` : ''}`, signal);
  },

  /**
   * Badge count for AppShell. Any authenticated; non-stewards see own
   * pending count, stewards see all.
   */
  badgeCount(slug: string, signal?: AbortSignal): Promise<AdminBadgeCountResponse> {
    return getJson(slug, `/admin-review/badge-count`, signal);
  },

  /** Archive an organization (soft-delete; sets is_active=false). */
  archiveProvider(slug: string, providerId: string, signal?: AbortSignal): Promise<unknown> {
    return deleteJson(
      slug,
      `/providers/${encodeURIComponent(providerId)}`,
      signal,
    );
  },

  /** Restore an archived organization. */
  restoreProvider(slug: string, providerId: string, signal?: AbortSignal): Promise<unknown> {
    return postNoBody(
      slug,
      `/providers/${encodeURIComponent(providerId)}/restore`,
      signal,
    );
  },

  /** Archive an individual (soft-delete; sets is_active=false). */
  archiveIndividual(slug: string, individualId: string, signal?: AbortSignal): Promise<unknown> {
    return deleteJson(
      slug,
      `/individuals/${encodeURIComponent(individualId)}`,
      signal,
    );
  },

  /** Restore an archived individual. */
  restoreIndividual(slug: string, individualId: string, signal?: AbortSignal): Promise<unknown> {
    return postNoBody(
      slug,
      `/individuals/${encodeURIComponent(individualId)}/restore`,
      signal,
    );
  },
};

// =====================================================================
// Cache invalidation helper (design plan r2 §7.8)
// =====================================================================
//
// Every mutation that emits change_log / creates provider_notes / writes
// suggested_edits should call invalidateAdminReviewKeys in its
// onSuccess hook so the Recent activity feed + AppShell badge stay
// fresh. Centralizes the query-key shape so the contract is one place.
//
// Note: TanStack invalidateQueries with a 2-element prefix matches all
// keys starting with that prefix — both `['admin-review', 'timeline']`
// AND `['admin-review', 'timeline', tenantSlug, actorFilter]` invalidate
// together. So callers don't need to enumerate per-actor variants.

export async function invalidateAdminReviewKeys(
  queryClient: QueryClient,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['admin-review', 'timeline'] }),
    queryClient.invalidateQueries({ queryKey: ['admin-review', 'badge-count'] }),
  ]);
}

/**
 * Subset of invalidateAdminReviewKeys for mutations that don't change
 * the pending-suggestions count (e.g. note creation, verifyIndividual).
 * Cheaper — skips the badge-count refetch.
 */
export async function invalidateAdminReviewTimelineOnly(
  queryClient: QueryClient,
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ['admin-review', 'timeline'] });
}

/**
 * Phase 1.6 — Team-tab mutation invalidator. Invalidates the team
 * query (list of members + pending grants) + the timeline (role-change
 * events flow there now). Does NOT invalidate badge-count — Team tab
 * mutations don't affect the pending-suggestions count per design
 * plan §8.
 */
export async function invalidateTeamKeys(
  queryClient: QueryClient,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['admin-review', 'team'] }),
    queryClient.invalidateQueries({ queryKey: ['admin-review', 'timeline'] }),
  ]);
}
